<script setup lang="ts">
import { type FormKitFrameworkContext, reset } from '@formkit/core'
import { getValidationMessages } from '@formkit/validation'
import { computed, ref, watch } from 'vue'
import Alert from '../Alert.vue'

const props = defineProps<{
  context: FormKitFrameworkContext
}>()

const wasDirty = ref(false)
const initialSuccessMessage = computed(() =>
  !wasDirty.value ? props.context.attrs['initial-success-message'] : undefined
)
const firstErrorMessage = ref<string>()
const root = props.context.node.root
const dirty = computed(() => root.context?.state.dirty)
const successMessage = computed((): string | undefined =>
  !dirty.value ? root.context?.attrs.successMessage : undefined
)
const type = computed(() => (initialSuccessMessage.value || successMessage.value ? 'success' : 'error'))
const loading = computed(() => root.context?.state.loading)
const message = computed(
  () =>
    initialSuccessMessage.value ??
    (!dirty.value && !loading.value ? (successMessage.value ?? firstErrorMessage.value) : undefined)
)

let submitted = false

// We want:
// 1. User submits invalid form
// 2. This component displays first error
// 3. User starts entering data (eg typing in input)
// 4. This component is hidden
// To track user entering data, we must use dirty state of form, but formkit
// does not set this back to false on submit, so this is what we are doing here
const onSubmitInvalid = root.props.onSubmitInvalid
root.props.onSubmitInvalid = async () => {
  if (submitted) {
    submitted = false
    return
  }
  submitted = true
  const [firstError] = getValidationMessages(root).values()
  firstErrorMessage.value = firstError?.[0]?.value as string
  setNotDirty()
  await new Promise(x => setTimeout(x, 0)) // wait for dirty update
  root.submit() // submit to get input errors back
  onSubmitInvalid?.()
}

function setNotDirty() {
  reset(root, root.value)
}

watch(
  () => dirty.value,
  (value?: boolean) => {
    if (value) {
      //If the form is marked dirty, we should remove any previous information about success and failure
      firstErrorMessage.value = undefined
      root.context!.attrs.successMessage = undefined
      wasDirty.value = true
    }
  }
)

watch(
  () => loading.value,
  (value?: boolean) => {
    if (!value) {
      const messages = Object.values(root.context?.messages ?? {})
      const errorMessages = messages.filter(x => x.type === 'error')
      const firstError = errorMessages[0]?.value
      if (typeof firstError === 'string') {
        firstErrorMessage.value = firstError
      }
      setNotDirty()
    }
  }
)
</script>

<template>
  <Alert v-if="message" :type="type" class="mb-2">
    {{ message }}
  </Alert>
</template>
